import { useEffect, useRef, useState } from 'react'

import { TRANSCRIPT_UNLOCK_TRIES, lockOf, readLocalUnlock, rememberLocalUnlock, transcriptLock } from '../lib/transcriptLock'
import { listHistory } from '../services/lessons'
import { useAuthStore } from '../store/authStore'

/**
 * The transcript lock of the lesson on screen (lib/transcriptLock.js): the
 * server's answer from `lessons_detail`, then — after every send, when a
 * verdict may have moved it — the learner's history of the lesson read
 * again by the same rule, as the Android app does. A server without the
 * field (older than R-lock-b) is answered from the history alone. Until one
 * of the two answers, the text stays locked.
 */
export function useTranscriptLock(lesson, sendsDone, { wrongReps = 0 } = {}) {
  const lessonId = lesson?.id ?? null
  const server = lesson?.transcript ?? null
  const [fromHistory, setFromHistory] = useState(null)
  // Owner rule 2026-10-14: three repetitions the live text did not match open
  // the text too, and it stays open for the lesson — kept per learner on the
  // device (the server never sees a repetition).
  const userId = useAuthStore((s) => s.user?.id ?? null)
  const [opened, setOpened] = useState(() => readLocalUnlock(userId, lessonId))
  useEffect(() => {
    setOpened(readLocalUnlock(userId, lessonId))
  }, [userId, lessonId])
  useEffect(() => {
    if (wrongReps < TRANSCRIPT_UNLOCK_TRIES || !lessonId) return
    rememberLocalUnlock(userId, lessonId)
    setOpened(true)
  }, [wrongReps, userId, lessonId])

  useEffect(() => {
    if (!lessonId) return undefined
    // The server's answer covers the load; the history is for what came after it.
    if (server && sendsDone === 0) return undefined
    let live = true
    listHistory({ lessonId })
      .then((data) => {
        if (live) setFromHistory({ lessonId, lock: transcriptLock(data?.items ?? []) })
      })
      .catch(() => {})
    return () => {
      live = false
    }
  }, [lessonId, server, sendsDone])

  return lockOf(server, fromHistory?.lessonId === lessonId ? fromHistory.lock : null, { wrongReps, unlocked: opened })
}

/** How many sends have finished on this page — the moments `analyzing` falls back to false. */
export function useSendsDone(analyzing) {
  const [count, setCount] = useState(0)
  const was = useRef(analyzing)
  useEffect(() => {
    if (was.current && !analyzing) setCount((value) => value + 1)
    was.current = analyzing
  }, [analyzing])
  return count
}
